// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Heart, Pill, MessageSquare, Clock, RefreshCcw } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, EmptyState, formatTimestamp, ClinicalStatePill } from '../../components/workflow/StatePills'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// ASP-23: severe-polypharmacy attention threshold. The watchlist itself surfaces 5+ active Rx;
// rows at or above this count are flagged (amber + ★) as needing interaction/adherence review.
const POLYPHARMACY_ALERT = 10

// D-UI-2: a funnel on every column. Each value is what the cell shows; dates stay raw.
// The state labels mirror ClinicalStatePill (StatePills.tsx does not export them); an unknown
// code falls back to the code itself, exactly as the pill does.
const CLINICAL_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', verified: 'Verified', ready_to_fill: 'Ready to fill',
  dispensed: 'Dispensed', out_for_delivery: 'Out for delivery', delivered: 'Delivered',
  on_hold: 'On hold', cancelled: 'Cancelled', rejected: 'Rejected',
}
const PA_FUNNELS: FunnelColumn<api.ClinicalDashboard['pa_stuck_48h'][number]>[] = [
  { key: 'rx_number', label: 'Rx #', value: r => r.rx_number },
  { key: 'drug_name', label: 'Drug', value: r => r.drug_name },
  { key: 'patient_name', label: 'Patient', value: r => r.patient_name },
  { key: 'payer_state', label: 'State', value: r => r.payer_state },
  { key: 'hours_pending', label: 'Pending', value: r => `${Math.floor(r.hours_pending / 24)}d ${r.hours_pending % 24}h` },
]
const REFILL_FUNNELS: FunnelColumn<api.ClinicalDashboard['refills_overdue_24h'][number]>[] = [
  { key: 'drug_name', label: 'Drug', value: r => r.drug_name },
  { key: 'patient_name', label: 'Patient', value: r => r.patient_name },
  { key: 'requested_at', label: 'Requested', value: r => r.requested_at },
  { key: 'hours_overdue', label: 'Overdue', value: r => `${r.hours_overdue}h` },
]
const HIGH_ALERT_FUNNELS: FunnelColumn<api.ClinicalDashboard['high_alert'][number]>[] = [
  { key: 'drug_name', label: 'Drug', value: r => r.drug_name },
  { key: 'patient_name', label: 'Patient', value: r => r.patient_name },
  { key: 'schedule', label: 'Schedule', value: r => r.schedule ?? 'CTRL' },
  { key: 'state', label: 'State', value: r => CLINICAL_LABELS[r.state] ?? r.state },
]
const POLY_FUNNELS: FunnelColumn<api.ClinicalDashboard['polypharmacy'][number]>[] = [
  { key: 'patient_name', label: 'Patient', value: p => p.patient_name },
  { key: 'active_rx', label: 'Active Rx', value: p => p.active_rx },
]

export default function ClinicalDashboard() {
  const navigate = useNavigate()
  const [data, setData] = useState<api.ClinicalDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true); setError(null)
    api.getClinicalDashboard()
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [])

  // D-UI-2: hooks stay above the early returns; the rows are empty until the data arrives.
  const paFx = useTableFunnels(data?.pa_stuck_48h, PA_FUNNELS)
  const refillFx = useTableFunnels(data?.refills_overdue_24h, REFILL_FUNNELS)
  const alertFx = useTableFunnels(data?.high_alert, HIGH_ALERT_FUNNELS)   // filters ALL of them, then the top 10
  const polyFx = useTableFunnels(data?.polypharmacy, POLY_FUNNELS)

  if (loading) return <div style={pageStyle}><LoadingState label="Loading clinical data…" /></div>
  if (error)   return <div style={pageStyle}><ErrorBanner message={error} /></div>
  if (!data)   return null

  return (
    <div style={pageStyle}>
      <header style={{ marginBottom: 24 }}>
        <h1 style={titleStyle}>Clinical dashboard</h1>
        <p style={subtitleStyle}>Are we taking care of patients?</p>
      </header>

      {/* PA stuck (the most actionable section, top of page) */}
      <Section icon={Clock} title={`Prior auth stuck >48h (${data.pa_stuck_48h.length})`}>
        {data.pa_stuck_48h.length === 0 ? (
          <EmptyState message="No PA stuck longer than 48 hours." />
        ) : (
          <>
          {paFx.chips}
          <table {...paFx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Rx # {paFx.funnel('rx_number')}</th>
                <th style={thStyle}>Drug {paFx.funnel('drug_name')}</th>
                <th style={thStyle}>Patient {paFx.funnel('patient_name')}</th>
                <th style={thStyle}>State {paFx.funnel('payer_state')}</th>
                <th style={thNumStyle}>Pending {paFx.funnel('hours_pending')}</th>
              </tr>
            </thead>
            <tbody>
              {paFx.rows.map(r => (
                <tr key={r.rx_id} style={trClickable} onClick={() => navigate(`/rx/${r.rx_id}`)}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>{r.rx_number ?? '—'}</td>
                  <td style={tdStyle}>{r.drug_name}</td>
                  <td style={tdStyle}>{r.patient_name}</td>
                  <td style={tdStyle}>{r.payer_state}</td>
                  <td style={{ ...tdNumStyle, color: r.hours_pending > 168 ? '#dc2626' : '#a16207' }}>
                    <strong>{Math.floor(r.hours_pending / 24)}d {r.hours_pending % 24}h</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </Section>

      {/* Refills overdue */}
      <Section icon={RefreshCcw} title={`Refill requests overdue >24h (${data.refills_overdue_24h.length})`}>
        {data.refills_overdue_24h.length === 0 ? (
          <EmptyState message="All refill requests under 24 hours old." />
        ) : (
          <>
          {refillFx.chips}
          <table {...refillFx.tableProps} style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Drug {refillFx.funnel('drug_name')}</th>
                <th style={thStyle}>Patient {refillFx.funnel('patient_name')}</th>
                <th style={thStyle}>Requested {refillFx.funnel('requested_at')}</th>
                <th style={thNumStyle}>Overdue {refillFx.funnel('hours_overdue')}</th>
              </tr>
            </thead>
            <tbody>
              {refillFx.rows.map(r => (
                <tr key={r.request_id} style={trClickable} onClick={() => navigate(`/rx/${r.rx_id}`)}>
                  <td style={tdStyle}>{r.drug_name}</td>
                  <td style={tdStyle}>{r.patient_name}</td>
                  <td style={tdStyle}>{formatTimestamp(r.requested_at)}</td>
                  <td style={{ ...tdNumStyle, color: '#a16207' }}>
                    <strong>{r.hours_overdue}h</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </Section>

      {/* Open clinical questions */}
      <Section icon={MessageSquare} title={`Open clinical questions (${data.open_clinical_questions.length})`}>
        {data.open_clinical_questions.length === 0 ? (
          <EmptyState message="No open clinical questions on active prescriptions." />
        ) : (
          <ul style={listStyle}>
            {data.open_clinical_questions.map(q => (
              <li key={q.comment_id} style={liStyle} onClick={() => navigate(`/rx/${q.rx_id}`)} role="button">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <strong style={{ fontSize: 13 }}>{q.author_name}</strong>
                  <span style={mutedSm}>{formatTimestamp(q.created_at)}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 2 }}>
                  Rx {q.rx_number ?? '—'} · {q.drug_name} · {q.patient_name}
                </div>
                <div style={{ fontSize: 13, marginTop: 4, color: 'var(--color-text)' }}>{q.body}</div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* Two-col: high-alert + polypharmacy */}
      <div style={twoColStyle}>
        <Section icon={Pill} title={`Controlled substances active (${data.high_alert.length})`}>
          {data.high_alert.length === 0 ? (
            <EmptyState message="No controlled substances active." />
          ) : (
            <>
            {alertFx.chips}
            <table {...alertFx.tableProps} style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Drug {alertFx.funnel('drug_name')}</th>
                  <th style={thStyle}>Patient {alertFx.funnel('patient_name')}</th>
                  <th style={thStyle}>Schedule {alertFx.funnel('schedule')}</th>
                  <th style={thStyle}>State {alertFx.funnel('state')}</th>
                </tr>
              </thead>
              <tbody>
                {alertFx.rows.slice(0, 10).map(r => (
                  <tr key={r.rx_id} style={trClickable} onClick={() => navigate(`/rx/${r.rx_id}`)}>
                    <td style={tdStyle}>{r.drug_name}</td>
                    <td style={tdStyle}>{r.patient_name}</td>
                    <td style={tdStyle}><span style={schedBadge}>{r.schedule ?? 'CTRL'}</span></td>
                    <td style={tdStyle}><ClinicalStatePill state={r.state} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </Section>

        <Section icon={Heart} title={`Polypharmacy watchlist (${data.polypharmacy.length})`}>
          {data.polypharmacy.length === 0 ? (
            <EmptyState message="No patients with 5+ active Rx." />
          ) : (
            <>
            {polyFx.chips}
            <table {...polyFx.tableProps} style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Patient {polyFx.funnel('patient_name')}</th>
                  <th style={thNumStyle}>Active Rx {polyFx.funnel('active_rx')}</th>
                </tr>
              </thead>
              <tbody>
                {polyFx.rows.map(p => {
                  // ASP-23: flag patients needing attention. Severe polypharmacy (>=10 concurrent
                  // active Rx) is a recognized high-risk interaction/adherence threshold — highlight
                  // the row (amber) AND mark with a ★ so it reads without relying on colour alone.
                  const alert = p.active_rx >= POLYPHARMACY_ALERT
                  return (
                    <tr key={p.patient_id}
                      style={alert ? { ...trClickable, background: '#FEF3C7' } : trClickable}
                      onClick={() => navigate(`/patients/${p.patient_id}`)}>
                      <td style={tdStyle}>
                        {alert && <span title={`${POLYPHARMACY_ALERT}+ active Rx — review for interactions`} style={{ color: '#B45309', fontWeight: 700, marginRight: 4 }}>★</span>}
                        {p.patient_name}
                      </td>
                      <td style={tdNumStyle}><strong style={alert ? { color: '#B45309' } : undefined}>{p.active_rx}</strong></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </>
          )}
          {data.polypharmacy.some(p => p.active_rx >= POLYPHARMACY_ALERT) && (
            <p style={{ margin: '8px 4px 0', fontSize: 11, color: 'var(--color-text-mute)' }}>
              <span style={{ color: '#B45309', fontWeight: 700 }}>★</span> {POLYPHARMACY_ALERT}+ active Rx — review for drug interactions and adherence.
            </p>
          )}
        </Section>
      </div>
    </div>
  )
}

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <section style={cardStyle}>
      <header style={cardHeaderStyle}>
        <Icon size={14} aria-hidden="true" />
        <h3 style={cardTitleStyle}>{title}</h3>
      </header>
      <div>{children}</div>
    </section>
  )
}

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', padding: '24px 24px 48px' }
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = { marginTop: 6, color: 'var(--color-text-soft)', fontSize: 14 }
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, marginBottom: 16, overflow: 'hidden',
}
const cardHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px',
  background: 'var(--color-surface-alt)', borderBottom: '1px solid var(--color-border)',
  color: 'var(--color-text-soft)',
}
const cardTitleStyle: React.CSSProperties = {
  margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
}
const twoColStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16,
}
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase',
  color: 'var(--color-text-mute)',
}
const thNumStyle: React.CSSProperties = { ...thStyle, textAlign: 'right' as const }
const tdStyle: React.CSSProperties = { padding: '10px 14px', borderBottom: '1px solid var(--color-border)' }
const tdNumStyle: React.CSSProperties = { ...tdStyle, textAlign: 'right' as const }
const trClickable: React.CSSProperties = { cursor: 'pointer' }
const listStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 }
const liStyle: React.CSSProperties = {
  padding: '12px 14px', borderBottom: '1px solid var(--color-border)', cursor: 'pointer',
}
const mutedSm: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
const schedBadge: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
  padding: '1px 6px', background: '#fef3c7', color: '#854d0e', borderRadius: 3,
}
